import {
  IconButton,
  IconButtonProps,
  Link,
  LinkProps,
} from '@material-ui/core';
import { AnchorButton, AnchorButtonProps } from '@superdispatch/ui-lab';
import React, { forwardRef } from 'react';
import { To, useHref, useNavigate } from 'react-router-dom';

interface BaseRouterLinkProps extends Pick<AnchorButtonProps, 'target'> {
  replace?: boolean;
  state?: unknown;
  to: To;
}

interface RouterLinkProps
  extends BaseRouterLinkProps, Omit<AnchorButtonProps, 'href'> {}

function useAnchorProps({
  to,
  target,
  replace,
  state,
  onClick,
}: RouterLinkProps): Pick<AnchorButtonProps, 'href' | 'target' | 'onClick'> {
  const navigate = useNavigate();
  const pathname = useHref(to);
  const href = pathname;

  /**
   * Implementation from react-router-dom/Link#onClick
   */
  function handleClick(event: React.MouseEvent<HTMLAnchorElement>): void {
    if (onClick) {
      try {
        onClick(event);
      } catch (error: unknown) {
        event.preventDefault();
        throw error;
      }
    }

    if (
      // onClick prevented default
      !event.defaultPrevented &&
      // ignore everything but left clicks
      event.button === 0 &&
      // let browser handle "target=_blank" etc.
      (!target || target === '_self') &&
      // ignore clicks with modifier keys
      !event.metaKey &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.shiftKey
    ) {
      event.preventDefault();

      navigate(to, { replace, state });
    }
  }

  return { href, target, onClick: handleClick };
}

interface LinkAnchorProps
  extends BaseRouterLinkProps, Omit<LinkProps, 'href' | 'target'> {}

export const LinkAnchor = forwardRef<HTMLAnchorElement, LinkAnchorProps>(
  ({ to, onClick, target, replace, state, ...props }, ref) => {
    const linkProps = useAnchorProps({ to, onClick, target, replace, state });

    return <Link {...props} {...linkProps} ref={ref} component="a" />;
  },
);

LinkAnchor.displayName = 'LinkAnchor';

interface LinkButtonProps
  extends BaseRouterLinkProps, Omit<AnchorButtonProps, 'href'> {}

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ to, onClick, target, replace, state, ...props }, ref) => {
    const linkProps = useAnchorProps({ to, onClick, target, replace, state });

    return <AnchorButton {...props} {...linkProps} ref={ref} />;
  },
);

LinkButton.displayName = 'LinkButton';

interface LinkIconButtonProps
  extends BaseRouterLinkProps, Omit<IconButtonProps<'a'>, 'href' | 'target'> {}

export const LinkIconButton = forwardRef<
  HTMLAnchorElement,
  LinkIconButtonProps
>(({ to, onClick, target, replace, state, ...props }, ref) => {
  const linkProps = useAnchorProps({ to, onClick, target, replace, state });
  return <IconButton {...props} {...linkProps} ref={ref} />;
});

LinkIconButton.displayName = 'LinkIconButton';
